import * as R from 'ramda';

import {
  ModalStep,
  ModalStepType,
  RouteDataState,
  RouteOptionsState,
} from '@atom/components/common/createAssetModal/CreateAssetModalContext';
import { Location } from '@atom/hooks/useCurrentLocation';
import { InventoryCategoryTree } from '@atom/types/category';
import { Schema } from '@atom/types/schema';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { isRouteStepIncomplete } from './assetRouteTab/assetRouteUtilities';

export const MODAL_STEPS: { [key: string]: ModalStepType } = {
  [ModalStep.TYPE]: {
    value: ModalStep.TYPE,
    title: 'Inventory Type',
  },
  [ModalStep.FOLDER]: {
    value: ModalStep.FOLDER,
    title: 'Inventory Folder',
  },
  [ModalStep.LOCATION]: {
    value: ModalStep.LOCATION,
    title: 'Location',
  },
  [ModalStep.ROUTE]: {
    value: ModalStep.ROUTE,
    title: 'Location Details',
  },
  [ModalStep.COST]: {
    value: ModalStep.COST,
    title: 'Cost Settings',
  },
  [ModalStep.ATTRIBUTES]: {
    value: ModalStep.ATTRIBUTES,
    title: 'Attributes',
  },
  [ModalStep.NAME]: {
    value: ModalStep.NAME,
    title: 'Name',
  },
};

export const getModalStepsArray = (schema: Schema) => {
  const steps = !schema
    ? [ModalStep.TYPE, ModalStep.FOLDER, ModalStep.NAME]
    : [
        ModalStep.TYPE,
        ...(!schema.lrsRequired ? [ModalStep.FOLDER] : []),
        ...(!R.isNil(schema?.locationType) ? [ModalStep.LOCATION] : []),
        ...(schema?.isMaterial ? [ModalStep.COST] : []),
        ...(schema.lrsRequired ? [ModalStep.ROUTE] : []),
        ...(!isNilOrEmpty(schema.attributes) ? [ModalStep.ATTRIBUTES] : []),
        ...(!schema.lrsRequired ? [ModalStep.NAME] : []),
      ];

  return steps.map(step => MODAL_STEPS[step]);
};

interface ValidationData {
  STEPS_ARRAY?: ModalStepType[];
  schema?: Schema;
  category?: InventoryCategoryTree;
  activeStep?: ModalStep;
  location?: Location;
  routeData?: RouteDataState;
  routeOptions?: RouteOptionsState;
  milePost?: number;
  milePostRanges?: number[][];
  isRouteSnapped?: boolean;
  rate?: number;
  unit?: string;
  isStockBased?: boolean;
  quantityOnHand?: number;
  name?: string;
  errorAttributeIds?: Set<string>;
}

// Evaluates whether the current step should be disabled
// The current step will be disabled if any previous step is also disabled
export const isCurrentStepDisabled = ({
  STEPS_ARRAY,
  schema,
  category,
  activeStep,
  location,
  routeData,
  routeOptions,
  milePost,
  milePostRanges,
  isRouteSnapped,
  rate,
  unit,
  isStockBased,
  quantityOnHand,
  name,
  errorAttributeIds,
}: ValidationData) => {
  // Map of all disable validation checks per given modal step
  const stepDisabledRules = {
    [ModalStep.TYPE]: isNilOrEmpty(schema),
    [ModalStep.FOLDER]: isNilOrEmpty(category),
    [ModalStep.LOCATION]: isNilOrEmpty(location),
    [ModalStep.ROUTE]: isRouteStepIncomplete(
      routeData,
      routeOptions,
      milePost,
      milePostRanges,
      isRouteSnapped,
    ),
    [ModalStep.COST]:
      R.isNil(rate) || !unit || (isStockBased && R.isNil(quantityOnHand)),
    [ModalStep.ATTRIBUTES]: errorAttributeIds.size > 0,
    [ModalStep.NAME]: isNilOrEmpty(name),
  };

  const currentStepIndex = R.findIndex(
    R.propEq(activeStep, 'value'),
    STEPS_ARRAY,
  );

  // Array of all steps up to the current step
  const stepsToValidate = R.dropLast(
    STEPS_ARRAY.length - currentStepIndex - 1,
    STEPS_ARRAY,
  );

  // Creates an array of validations, denoting whether the step at the given
  // index should be disabled or not
  const validatedSteps = stepsToValidate.map((step: ModalStepType) => {
    return stepDisabledRules[step.value];
  });

  // If any previous step (or current) step are disabled, the step is disabled
  return R.any(R.equals(true), validatedSteps);
};
